import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getRecapturePaymentWarnings, getRecaptureWorkspace, RECAPTURE_CHANGED_EVENT,
  type RecapturePaymentWarning, type RecaptureWorkspace
} from '@/services/recaptureService';

export function useRecaptureWorkspace() {
  const [workspace, setWorkspace] = useState<RecaptureWorkspace | null>(null);
  const [warnings, setWarnings] = useState<RecapturePaymentWarning[]>([]);
  const [error, setError] = useState('');
  const generation = useRef(0);
  const refresh = useCallback(async () => {
    const request = ++generation.current;
    try {
      const result = await getRecaptureWorkspace();
      if (request !== generation.current) {
        return;
      }
      setWorkspace(result);
      setWarnings(result.records);
      setError('');
    } catch (cause) {
      if (request !== generation.current) {
        return;
      }
      const failure = cause as { code?: string };
      if (failure.code === '42501') {
        setWorkspace(null);
        // Partners retain cash-payment access, with a minimal warning payload.
        try {
          const result = await getRecapturePaymentWarnings();
          if (request !== generation.current) {
            return;
          }
          setWarnings(result);
          setError('');
          return;
        } catch (warningError) {
          if (request !== generation.current) {
            return;
          }
          if ((warningError as { code?: string }).code === '42501') {
            setWarnings([]);
            setError('');
            return;
          }
        }
      }
      setError('Recapture history could not be refreshed.');
      throw cause;
    }
  }, []);
  useEffect(() => {
    const requests = generation;
    const update = () => {
      void refresh().catch(() => undefined);
    };
    update();
    window.addEventListener(RECAPTURE_CHANGED_EVENT, update);
    return () => {
      requests.current++;
      window.removeEventListener(RECAPTURE_CHANGED_EVENT, update);
    };
  }, [refresh]);
  return { workspace, warnings, error, refresh };
}
